import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Anchor, Button, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { userManagementService } from '@/api/services/UserManagementService';
import { getStatus, isNetworkError } from '@/api/errors';
import AuthCard from '@/components/auth/AuthCard';
import PasswordField from '@/components/auth/PasswordField';
import { NO_SPACES_PATTERN, PASSWORD_MIN_LENGTH, isBlank } from '@/lib/validators';
import { ROUTES } from '@/routes/paths';
import { useAuth } from '@/stores/authContext';
import type { EmployeeInvitationInfo } from '@/types/employee';

/** Причина, по которой приглашением нельзя воспользоваться. */
type InvitationProblem = 'expired' | 'accepted' | 'notFound' | 'unknown';

/**
 * Приём приглашения сотрудника: человек приходит по ссылке из письма, когда учётной записи у него
 * ещё нет. Задаёт пароль — и сразу попадает в кабинет, без отдельного входа.
 *
 * Истёкшее и уже принятое приглашение — не ошибка, а состояние: показываем спокойное объяснение и
 * следующий шаг, а не красный сбой (§11 «снисходительность к ошибкам»).
 */
export default function EmployeeInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [info, setInfo] = useState<EmployeeInvitationInfo | null>(null);
  const [problem, setProblem] = useState<InvitationProblem | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: { password: '' },
    validate: {
      password: (value: string) => {
        if (isBlank(value)) {
          return t('auth.validation.required');
        }
        if (value.length < PASSWORD_MIN_LENGTH) {
          return t('auth.validation.passwordMin');
        }
        if (!NO_SPACES_PATTERN.test(value)) {
          return t('auth.validation.passwordSpaces');
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (!id) {
      setProblem('notFound');
      setLoading(false);
      return;
    }
    let cancelled = false;
    userManagementService
      .validateEmployeeInvitation(id)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setInfo(data);
        // Признаки приходят в успешном ответе — страница объясняет, а не падает
        setProblem(data.expired ? 'expired' : data.accepted ? 'accepted' : null);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        // Сервис отвечает 204 на «не найдено» (общая конвенция user-management-service):
        // пустое тело транспорт превращает в ошибку с этим статусом.
        setProblem(getStatus(error) === 204 ? 'notFound' : 'unknown');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (values: { password: string }) => {
    if (!id) {
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await userManagementService.acceptEmployeeInvitation(id, values.password);
      loginWithTokens(tokens);
      notifications.show({ color: 'lime', message: t('auth.employeeInvitation.welcome') });
      navigate(ROUTES.APP, { replace: true });
    } catch (error: unknown) {
      if (isNetworkError(error)) {
        notifications.show({ color: 'red', message: t('auth.errors.generic') });
      } else {
        // Приглашение могло истечь или быть принято, пока человек заполнял форму:
        // 406 — просроченное/уже принятое, 204 — исчезнувшее.
        setProblem(getStatus(error) === 204 ? 'notFound' : 'expired');
      }
      form.setFieldValue('password', '');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthCard title={t('auth.employeeInvitation.title')}>
        <Stack align="center" py="xl">
          <Loader color="brand" />
        </Stack>
      </AuthCard>
    );
  }

  if (problem) {
    return (
      <AuthCard title={t('auth.employeeInvitation.title')}>
        <Alert
          variant="light"
          color={problem === 'accepted' ? 'brand' : 'red'}
          icon={<IconAlertCircle size={18} />}
          title={t(`auth.employeeInvitation.problem.${problem}.title`)}
        >
          <Stack gap="xs">
            <Text size="sm">{t(`auth.employeeInvitation.problem.${problem}.text`)}</Text>
            <Anchor component={Link} to={ROUTES.LOGIN} size="sm">
              {t('auth.employeeInvitation.toLogin')}
            </Anchor>
          </Stack>
        </Alert>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t('auth.employeeInvitation.title')}
      description={t('auth.employeeInvitation.description', { organization: info?.organizationName })}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Email показываем и отдаём менеджеру паролей (autocomplete username), но менять его
              нельзя: приглашение выписано на конкретный адрес. */}
          <TextInput
            label={t('auth.fields.email')}
            value={info?.email ?? ''}
            readOnly
            name="username"
            id="username"
            type="email"
            autoComplete="username"
          />

          {/* Одно поле пароля с показом вместо двух: браузер предлагает и сохраняет пароль,
              повторный ввод только увеличивает работу для всех (§8 «минимум полей»). */}
          <PasswordField
            label={t('auth.employeeInvitation.passwordLabel')}
            description={t('auth.employeeInvitation.passwordHint')}
            name="new-password"
            id="new-password"
            autoComplete="new-password"
            required
            key={form.key('password')}
            {...form.getInputProps('password')}
          />

          <Button type="submit" fullWidth loading={submitting}>
            {t('auth.employeeInvitation.submit')}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
